import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { useLang } from "@/i18n";
import { syncKeysOnLogin } from "@/utils/e2e";
import { isNativeIOS, appleNativeLogin } from "@/utils/appleAuth";

export default function Login() {
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("suspended") === "1") {
      toast.error("Il tuo account è stato sospeso. Contatta il supporto ADE.");
    }
  }, []);
  const navigate = useNavigate();
  const { t } = useLang();
  const [email, setEmail] = useState(() => localStorage.getItem('last_login_email') || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error("Inserisci la tua email");
      return;
    }
    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      toast.success(t("tempPwdSent"));
      setShowForgot(false);
    } catch (e) {
      toast.error("Errore, riprova");
    } finally {
      setForgotLoading(false);
    }
  };
  
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Inserisci email e password");
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      const token = response.data.token;
      localStorage.setItem('token', token);
      localStorage.setItem('last_login_email', email);
      
      // Store token in Service Worker for background sync
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'STORE_TOKEN',
          token: token
        });
      }
      
      // Sync E2E chat keys (restore from backup on new device, or create backup)
      try {
        const uid = response.data.user?.user_id || (await api.get('/auth/me')).data.user_id;
        await syncKeysOnLogin(uid, password);
      } catch (e) {
        // non-blocking
      }
      
      if (response.data.must_change_password) {
        toast.info(t("forcedChangeNote"));
        navigate('/app/change-password?force=1', { replace: true });
      } else if (response.data.user.onboarding_completed) {
        navigate('/app/home', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/app/nearby';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const [appleLoading, setAppleLoading] = useState(false);
  const handleAppleLogin = async () => {
    setAppleLoading(true);
    try {
      const data = await appleNativeLogin(api);
      if (data.user.onboarding_completed) {
        navigate('/app/home', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    } catch (error) {
      if (error?.response) {
        toast.error(error.response?.data?.detail || "Accesso con Apple non riuscito");
      }
      // native sheet cancelled / no token → silent
    } finally {
      setAppleLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center p-4">
        <button 
          onClick={() => navigate('/')}
          className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" strokeWidth={1.5} />
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 px-6 py-8">
        <div className="max-w-sm mx-auto">
          {/* Title */}
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              {t('welcomeBack')}
            </h1>
            <p className="text-gray-500">
              {t('loginSubtitle')}
            </p>
          </div>
          
          {/* Google Login */}
          <Button
            data-testid="google-login-btn"
            variant="outline"
            className="w-full h-12 rounded-full border-gray-200 mb-6 font-medium hover:bg-gray-50 active-scale"
            onClick={handleGoogleLogin}
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('continueWithGoogle')}
          </Button>
          
          {/* Apple Login (native iOS only) */}
          {isNativeIOS() && (
            <Button
              data-testid="apple-login-btn"
              className="w-full h-12 rounded-full bg-black text-white hover:bg-gray-900 -mt-3 mb-6 font-medium active-scale"
              onClick={handleAppleLogin}
              disabled={appleLoading}
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              {t('continueWithApple')}
            </Button>
          )}
          
          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-sm text-gray-400">{t('or')}</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>
          
          {/* Email Form */}
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-gray-700">{t('email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="nome@esempio.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl border-gray-200 bg-gray-50 focus:bg-white"
                data-testid="email-input"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm text-gray-700">{t('password')}</Label>
                <button
                  type="button"
                  onClick={() => { setForgotEmail(email); setShowForgot(true); }}
                  className="text-xs text-gray-500 hover:text-gray-900 underline"
                  data-testid="forgot-password-link"
                >
                  {t('forgotPasswordLink')}
                </button>
              </div>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-xl border-gray-200 bg-gray-50 focus:bg-white"
                data-testid="password-input"
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-gray-900 text-white hover:bg-gray-800 font-medium active-scale"
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="animate-pulse">{t('loginInProgress')}</span>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  {t('loginWithEmail')}
                </>
              )}
            </Button>
          </form>
          
          {/* Register Link */}
          <p className="text-center mt-6 text-sm text-gray-500">
            {t('noAccount')}{" "}
            <Link to="/register" className="text-gray-900 font-medium hover:underline">
              {t('register')}
            </Link>
          </p>
        </div>
      </div>

      {/* Forgot password modal */}
      {showForgot && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-6" data-testid="forgot-password-modal">
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm space-y-4">
            <h3 className="font-semibold text-gray-900">{t('forgotPasswordTitle')}</h3>
            <p className="text-xs text-gray-500">{t('forgotPasswordDesc')}</p>
            <Input
              type="email"
              placeholder="nome@esempio.com"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              className="h-12 rounded-xl border-gray-200 bg-gray-50"
              data-testid="forgot-email-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 h-11 rounded-full" onClick={() => setShowForgot(false)}>
                Annulla
              </Button>
              <Button
                className="flex-1 h-11 rounded-full bg-gray-900 text-white"
                onClick={handleForgotPassword}
                disabled={forgotLoading}
                data-testid="forgot-submit-btn"
              >
                {forgotLoading ? "..." : "Invia"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
